import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormContext } from 'react-form';
import { CircularProgress } from '@material-ui/core';
import { Button, type ButtonProps as MuiButtonProps } from '@popmenu/admin-ui';

import { makeStyles } from '~/utils/withStyles';
import { executeWithProgressBar } from '~/utils/postponed';
import { classNames } from '../../../utils/withStyles';
import formStyles from '../../../assets/jss/shared/formStyles';
import { whenNoDebouncingInTextFields } from './withTextFieldDebouncer';

import Grid from '../../../shared/Grid';

export type SubmitGroupProps = {
  block?: boolean,
  ButtonProps?: Partial<MuiButtonProps & { component?: React.ElementType }>,
  CancelButtonProps?: Partial<React.ComponentProps<typeof Button>>,
  children?: React.ReactNode,
  className?: string,
  'data-cy'?: string,
  'data-tour-id'?: string,
  disableOnInvalid?: boolean,
  disabled?: boolean,
  id?: string,
  inline?: boolean,
  justifyContent?: 'center' | 'flex-end' | 'flex-start',
  manualSubmit?: boolean,
  loading?: boolean,
  onCancel?: React.MouseEventHandler<HTMLButtonElement>,
  onClick?: React.MouseEventHandler<HTMLButtonElement>,
  size?: 'small' | 'medium' | 'large' | 'xl' | undefined,
  submitGroupButtonContainerStyle?: string,
  title?: React.ReactNode | string,
  variant?: 'outlined' | 'contained' | 'text',
};

const useStyles = makeStyles(formStyles);

const resolveVariant = (variant: SubmitGroupProps['variant']) => {
  switch (variant) {
    case 'outlined':
      return 'secondary';
    case 'contained':
      return 'primary';
    case 'text':
      return 'text';
    default:
      return 'primary';
  }
};

const resolveSize = (size: SubmitGroupProps['size']) => {
  if (size === undefined) return undefined;
  switch (size) {
    case 'small':
      return 'small';
    case 'medium':
      return 'medium';
    case 'large':
      return 'large';
    case 'xl':
      return 'large';
    default:
      return undefined;
  }
};

const SubmitGroup = ({
  ButtonProps = {},
  block = false,
  loading: loadingProp = false,
  CancelButtonProps = {},
  children,
  className,
  'data-cy': dataCy = 'submit',
  'data-tour-id': dataTourId,
  disableOnInvalid = true,
  disabled = false,
  id,
  inline = false,
  justifyContent = 'flex-end',
  manualSubmit = false,
  onCancel,
  onClick,
  size,
  submitGroupButtonContainerStyle,
  title,
  variant = 'contained',
}: SubmitGroupProps) => {
  // Loading state from form metadata

  const { meta: { isValid, isSubmitting }, handleSubmit } = useFormContext();
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const loading = loadingProp || isSubmitting;
  const classes = useStyles();
  const [textFieldDebouncing, setTextFieldDebouncing] = useState(false);

  const type = ButtonProps.type || (manualSubmit && typeof onClick === 'function' ? 'button' : 'submit');

  const formattedTitle = title || <FormattedMessage defaultMessage="Save" />;
  const { disabled: cancelDisabled = disabled, ...cancelButtonProps } = CancelButtonProps;

  const onClickRef = useRef<React.MouseEventHandler<HTMLButtonElement>>();
  onClickRef.current = onClick;

  const debouncedOnClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (ButtonProps?.component) {
      if (typeof onClickRef.current === 'function') {
        onClickRef.current(e);
      }
      return;
    }

    e.preventDefault(); // prevent triggering form submission
    const target = e.target as HTMLElement;
    if (!target) {
      return;
    }

    setTextFieldDebouncing(true);
    executeWithProgressBar(() => {
      void whenNoDebouncingInTextFields().then(() => {
        if (typeof onClickRef.current === 'function') {
          onClickRef.current(e);
        }

        if (type === 'submit') {
          // Replicate browsers default behavior: only submit the form if the button is inside a form.
          if (target.closest('form')) {
            handleSubmitRef.current(e);
          }
        }

        // React form is lazy in setting |loading| prop in time.
        // This makes it possible to submit the same form multiple times when clicking the same button fast.
        setTimeout(() => {
          setTextFieldDebouncing(false);
        }, 400);
      });
    });
  }, [ButtonProps?.component, type]);

  // Submit button
  const button = (
    <Button
      fullWidth={block}
      className={classNames(classes.submitButton, className)}
      data-cy={dataCy}
      data-tour-id={dataTourId}
      data-testid={'submit-group-button'}
      disabled={disabled || loading || (disableOnInvalid ? !isValid : false) || textFieldDebouncing}
      id={id}
      onClick={debouncedOnClick}
      size={resolveSize(size)}
      type={type}
      variant={resolveVariant(variant)}
      disableFocusRipple
      {...ButtonProps}
    >
      {loading && (<CircularProgress className={classes.submitButtonSpinner} size={16} />)} {formattedTitle}
    </Button>
  );
  if (inline) {
    return button;
  }

  // Cancel button
  let cancelButton = null;
  if (typeof onCancel === 'function') {
    cancelButton = (
      <Button
        className={classes.cancelButton}
        data-cy="cancel"
        data-testid={'cancel-button'}
        disabled={loading || textFieldDebouncing || cancelDisabled}
        onClick={onCancel}
        size={resolveSize(size)}
        type="button"
        variant="text"
        {...cancelButtonProps}
      >
        <FormattedMessage defaultMessage="Cancel" />
      </Button>
    );
  }
  return (
    <Grid
      className={classNames(className, classes.submitGroup)}
      container
      inline={!block}
      justifyContent={justifyContent}
    >
      {cancelButton && (
        <Grid item>
          {cancelButton}
        </Grid>
      )}
      <Grid item xs={block ? 12 : undefined} className={classNames([size === 'xl' ? classes.extraLarge : null, submitGroupButtonContainerStyle])}>
        {button}
      </Grid>
      {children && (
        <Grid item>
          {children}
        </Grid>
      )}
    </Grid>
  );
};

export default SubmitGroup;
